import { Box } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { addMember } from '../api';
import AccountForm from '../components/AccountForm';
import LoadingSpinner from '../components/LoadingSpinner';
import { AuthContext } from '../context/AuthContext';
import { passwordMatchCheck } from '../utils';
import { IAccountInput, IAccountResult } from '../utils/types';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';

type AccountContainerProps = {
  user?: IAccountResult;
};

export default function AccountContainer({ user }: AccountContainerProps) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { setToken } = useContext(AuthContext);
  const [newUserToken, setNewUserToken] = useState('');
  const [accountInfo, setAccountInfo] = useState<IAccountResult>();
  const [submitting, setSubmitting] = useState(false);
  const showSnackbar = useAlertSnackbar();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenFromQuery = queryParams.get('newUserToken');
    if (tokenFromQuery) {
      setNewUserToken(tokenFromQuery);
    }
  }, [location.search]);

  useEffect(() => {
    setAccountInfo(user);
  }, [user]);

  const onAccountInfoChange = (accountInput: IAccountInput) => {
    accountInfo &&
      setAccountInfo({ ...accountInfo, ...accountInput } as IAccountResult);
  };

  const handleSubmit = async (password: string, passwordAgain: string) => {
    try {
      if (!passwordMatchCheck(password, passwordAgain)) {
        showSnackbar(t('accountContainer.passwordMatchErrorMessage'), 'error');
        return;
      }

      if (!accountInfo?.individual?.gender) {
        showSnackbar(t('accountContainer.genderEmptyErrorMessage'), 'error');
        return;
      }

      setSubmitting(true);
      const res = await addMember(
        {
          firstName: accountInfo?.individual?.firstName,
          lastName: accountInfo?.individual?.lastName,
          birthDate: accountInfo?.individual?.birthDate,
          gender: accountInfo?.individual?.gender,
          phoneNumber: accountInfo?.phoneNumber,
          email: accountInfo?.email,
          citizenshipCountryId: accountInfo?.citizenshipCountryId,
          citizenshipNumber: accountInfo?.citizenshipNumber,
          password: password,
          membershipContractApproved: true,
        },
        newUserToken,
      );
      setSubmitting(false);
      if (res.status === 201) {
        const tokenRes = await res.text();
        localStorage.setItem('id_token', tokenRes);
        setToken(tokenRes);

        showSnackbar(
          t('accountContainer.creteAccountSuccessMessage'),
          'success',
        );

        setTimeout(() => {
          nav('/');
        }, 3000);
      } else {
        const data = await res.json();
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setSubmitting(false);
      showSnackbar(
        e.detail || t('accountContainer.creteAccountErrorMessage'),
        'error',
      );
    }
  };

  return (
    <>
      {submitting && (
        <LoadingSpinner loadingText={t('accountContainer.submitting')} />
      )}
      {!submitting && (
        <Box sx={{ maxWidth: 480, mt: 4 }}>
          <AccountForm
            onAccountInfoChange={onAccountInfoChange}
            accountInfo={accountInfo}
            onSubmit={handleSubmit}
          />
        </Box>
      )}
    </>
  );
}
